import { useEffect, useId, useState } from 'react'
import { Button } from '../../components/ui/Button'

export interface QuantityMultiplierProps {
  readonly disabled?: boolean
  /** The factor currently applied to every cart line (`1` = none applied).
   * Owned by CartPage so it survives this control's re-renders and drives
   * the badge — the applied factor is cart state, not throwaway control
   * state, so it must be lifted up. */
  readonly appliedMultiplier: number
  /** Applies `multiplier` to every cart line. The caller (CartPage) computes
   * each line's next quantity from its preserved BASE quantity
   * (`base × multiplier`), never from the already-multiplied value, so
   * re-applying a different factor replaces rather than COMPOUNDS it (3×
   * then 4× lands on 4×, not 12×). There is no bulk-multiply endpoint on the
   * Stage-1a contract, only the existing per-line quantity PATCH, so this is
   * a client-side loop over the same operation the quantity stepper uses. */
  readonly onApply: (multiplier: number) => void
  /** Restores every line to its captured base quantity and clears the factor
   * back to 1×. */
  readonly onReset: () => void
}

const MIN_MULTIPLIER = 1
const MAX_MULTIPLIER = 100

/**
 * The Cart screen's "multiply all cart quantities" utility (Cart redesign,
 * bottom-right of the order-summary column): a bounded stepper + an
 * explicit Apply (never auto-applies on every +/- click, so a buyer can
 * dial in e.g. "10x" before it touches a single line).
 *
 * The applied factor is persistent, lifted to CartPage (`appliedMultiplier`):
 * after Apply the badge keeps showing the factor in force (e.g. "3×") rather
 * than snapping back to 1×, and a Reset restores every line to its original
 * quantity. Compounding is prevented not by resetting the control but by
 * CartPage always computing `base × factor`, so this control can safely stay
 * on the applied value.
 */
export function QuantityMultiplier({
  disabled = false,
  appliedMultiplier,
  onApply,
  onReset,
}: QuantityMultiplierProps) {
  // The factor the buyer is dialing in. Seeded from — and re-synced to — the
  // applied factor so the control always opens on the current state and snaps
  // to 1× when the parent resets.
  const [draft, setDraft] = useState(appliedMultiplier)
  const labelId = useId()

  useEffect(() => {
    setDraft(appliedMultiplier)
  }, [appliedMultiplier])

  function clamp(next: number) {
    return Math.min(MAX_MULTIPLIER, Math.max(MIN_MULTIPLIER, next))
  }

  const isApplied = appliedMultiplier > MIN_MULTIPLIER

  return (
    <div className="cart-qty-multiplier">
      <span id={labelId} className="caption-text">
        Multiply all cart quantities
      </span>
      <div className="cart-qty-multiplier-control">
        <button
          type="button"
          className="qty-stepper-btn"
          aria-label="Decrease multiplier"
          disabled={disabled || draft <= MIN_MULTIPLIER}
          onClick={() => setDraft((value) => clamp(value - 1))}
        >
          −
        </button>
        <span aria-labelledby={labelId} className="cart-qty-multiplier-value">
          {draft}×
        </span>
        <button
          type="button"
          className="qty-stepper-btn"
          aria-label="Increase multiplier"
          disabled={disabled || draft >= MAX_MULTIPLIER}
          onClick={() => setDraft((value) => clamp(value + 1))}
        >
          +
        </button>
      </div>
      <Button
        variant="secondary"
        size="sm"
        // Disabled when the dialed factor already matches what's applied —
        // nothing new to apply, and it blocks a redundant re-PATCH of the
        // same factor.
        disabled={disabled || draft === appliedMultiplier}
        onClick={() => onApply(draft)}
      >
        Apply
      </Button>
      {isApplied && (
        <Button variant="outline" size="sm" disabled={disabled} onClick={() => onReset()}>
          Reset
        </Button>
      )}
    </div>
  )
}
